import { useMutation, useQuery } from '@tanstack/react-query';
import { Col, Flex, Form, Row, TimePicker } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import _ from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { generalConfigApi, recruitmentTypeApi } from '../../../../apis';
import {
  CreateWorkHoursDto,
  SaveWorkHoursDto,
  UpdateGeneralWorkingScheduleDto,
  WorkHoursResponseDto,
  WorkHoursTypeEnum,
} from '../../../../apis/client-axios';
import ButtonWrap from '../../../../components/ButtonWrap';
import InputWrap from '../../../../components/InputWrap';
import SvgAddPrimary from '../../../../components/svg/SvgAddPrimary';
import SvgDelete from '../../../../components/svg/SvgDelete';
import TableWrap from '../../../../components/TableWrap';
import ToastError from '../../../../components/Toast/ToastError';
import ToastSuccess from '../../../../components/Toast/ToastSuccess';
import Widget from '../../../../components/Widget';
import { QUERY_KEY, WORK_HOURS } from '../../../../constants/QueryKey';
import './style.scss';

dayjs.extend(customParseFormat);

enum WorkHoursPosition {
  START = 'START',
  END = 'END',
}

const TIME_FORMAT = 'HH:mm';
const fixedLeftColumnWidth = 216;
const fixedRightColumnWidth = 38;
const defaultOtherColumnWidth = 299;

const WorkHours = () => {
  const intl = useIntl();

  const [form] = Form.useForm();

  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const isChangeBreakHourRef = useRef<boolean>(false);

  const [dataSource, setDataSource] = useState<WorkHoursResponseDto[]>([]);

  const {
    data: workHoursData,
    isLoading: isLoadingWorkHours,
    refetch,
  } = useQuery({
    queryKey: [WORK_HOURS],
    queryFn: () => generalConfigApi.generalConfigControllerGetWorkHours(),
  });

  const { data: recruitmentTypeData, isLoading: isLoadingRecruitmentType } = useQuery({
    queryKey: [QUERY_KEY.USER_TYPE_LIST],
    queryFn: () => {
      return recruitmentTypeApi.recruitmentTypeControllerGetAll(1, undefined, undefined, undefined, undefined);
    },
  });

  const saveWorkHoursMutation = useMutation({
    mutationFn: (payload: SaveWorkHoursDto) => generalConfigApi.generalConfigControllerSaveWorkHours(payload),
    onSuccess: () => {
      refetch();
      isChangeBreakHourRef.current = false;
      ToastSuccess({ content: intl.formatMessage({ id: 'workHours.save.success' }) });
    },
    onError: () => {
      ToastError({ content: intl.formatMessage({ id: 'workHours.save.failed' }) });
    },
  });

  const handleDisabledTime = (_: Dayjs, formKey: string) => {
    const [workHourType, position, index, recruitmentTypeId] = formKey.split('.') || [];
    const fields = form.getFieldsValue();

    const workHoursStart = fields[`${WorkHoursTypeEnum.WorkHours}.${WorkHoursPosition.START}.0.${recruitmentTypeId}`];
    const workHoursEnd = fields[`${WorkHoursTypeEnum.WorkHours}.${WorkHoursPosition.END}.0.${recruitmentTypeId}`];

    const isOvernightShift =
      workHoursStart &&
      workHoursEnd &&
      (dayjs(workHoursStart).isAfter(dayjs(workHoursEnd)) || dayjs(workHoursStart).isSame(dayjs(workHoursEnd)));

    return {
      disabledHours: () => {
        let hours: number[] = [];

        if (workHourType === WorkHoursTypeEnum.BreakHours) {
          const prevFieldValue =
            fields[
              `${workHourType}.${position === WorkHoursPosition.START ? WorkHoursPosition.END : WorkHoursPosition.START}.${position === WorkHoursPosition.START ? +index - 1 : index}.${recruitmentTypeId}`
            ];
          const nextFieldValue =
            fields[
              `${workHourType}.${position === WorkHoursPosition.START ? WorkHoursPosition.END : WorkHoursPosition.START}.${position === WorkHoursPosition.START ? index : +index + 1}.${recruitmentTypeId}`
            ];

          if (isOvernightShift && workHoursStart && workHoursEnd) {
            const workStartHour = dayjs(workHoursStart).hour();
            const workEndHour = dayjs(workHoursEnd).hour();
            const prevFieldValue =
              fields[
                `${workHourType}.${position === WorkHoursPosition.START ? WorkHoursPosition.END : WorkHoursPosition.START}.${position === WorkHoursPosition.START ? +index - 1 : index}.${recruitmentTypeId}`
              ];
            const prevFieldHour = prevFieldValue ? dayjs(prevFieldValue).hour() : null;

            if (position === WorkHoursPosition.END && prevFieldHour !== null) {
              for (let i = 0; i < 24; i++) {
                if (prevFieldHour <= workEndHour) {
                  if (i < prevFieldHour || i > workEndHour) {
                    hours.push(i);
                  }
                } else {
                  if (i < prevFieldHour && i > workEndHour) {
                    hours.push(i);
                  }
                }
              }
            } else {
              for (let i = 0; i < 24; i++) {
                if (workStartHour <= workEndHour) {
                  if (i < workStartHour || i > workEndHour) {
                    hours.push(i);
                  }
                } else {
                  if (i > workEndHour && i < workStartHour) {
                    hours.push(i);
                  }
                }
              }
            }
          } else {
            if (prevFieldValue) {
              for (let i = 0; i < dayjs(prevFieldValue).hour(); i++) {
                hours.push(i);
              }
            }

            if (nextFieldValue) {
              for (let i = dayjs(nextFieldValue).hour() + 1; i < 24; i++) {
                hours.push(i);
              }
            }
          }

          hours = Array.from(new Set(hours));
        }

        return hours;
      },
      disabledMinutes: (selectedHour: number) => {
        let minutes: number[] = [];

        if (workHourType === WorkHoursTypeEnum.BreakHours) {
          const prevFieldValue =
            fields[
              `${workHourType}.${position === WorkHoursPosition.START ? WorkHoursPosition.END : WorkHoursPosition.START}.${position === WorkHoursPosition.START ? +index - 1 : index}.${recruitmentTypeId}`
            ];
          const nextFieldValue =
            fields[
              `${workHourType}.${position === WorkHoursPosition.START ? WorkHoursPosition.END : WorkHoursPosition.START}.${position === WorkHoursPosition.START ? index : +index + 1}.${recruitmentTypeId}`
            ];

          if (isOvernightShift && workHoursStart && workHoursEnd) {
            const workStartHour = dayjs(workHoursStart).hour();
            const workEndHour = dayjs(workHoursEnd).hour();

            if (selectedHour === workStartHour) {
              for (let i = 0; i < dayjs(workHoursStart).minute(); i++) {
                minutes.push(i);
              }
            } else if (selectedHour === workEndHour) {
              for (let i = dayjs(workHoursEnd).minute() + 1; i < 60; i++) {
                minutes.push(i);
              }
            }
          } else {
            if (prevFieldValue && selectedHour === dayjs(prevFieldValue).hour()) {
              for (let i = 0; i <= dayjs(prevFieldValue).minute(); i++) {
                minutes.push(i);
              }
            }

            if (nextFieldValue && selectedHour === dayjs(nextFieldValue).hour()) {
              for (let i = dayjs(nextFieldValue).minute() + 1; i < 60; i++) {
                minutes.push(i);
              }
            }
          }
        }

        return minutes;
      },
    };
  };

  const columns = useMemo(() => {
    const recruitmentTypes = Array.isArray(recruitmentTypeData?.data?.data?.content)
      ? recruitmentTypeData?.data?.data?.content
      : [];

    if (!Array.isArray(recruitmentTypes) || !recruitmentTypes?.length) return [];

    const tableWidth = Number(tableWrapperRef.current?.clientWidth);
    const otherColumnsWidth =
      Number(tableWidth - fixedLeftColumnWidth - fixedRightColumnWidth) / Number(recruitmentTypes?.length || 1);

    const _columns: ColumnsType = recruitmentTypes.map((recruitmentType) => ({
      dataIndex: recruitmentType.type,
      key: recruitmentType.id,
      title: recruitmentType.type,
      ellipsis: true,
      width: otherColumnsWidth < defaultOtherColumnWidth ? defaultOtherColumnWidth : otherColumnsWidth,
      render: (value, record) => {
        const [, position] = record?.key?.split('.');
        const name = `${record?.key || ''}.${record?.index || 0}.${value?.recruitmentTypeId || ''}`;

        return (
          <Form.Item
            name={name}
            className="mb-0"
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (
                    !value &&
                    getFieldValue(
                      name.replace(
                        position,
                        position === WorkHoursPosition.START ? WorkHoursPosition.END : WorkHoursPosition.START,
                      ),
                    )
                  )
                    return Promise.reject(intl.formatMessage({ id: 'workHours.required' }));
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <TimePicker
              format={TIME_FORMAT}
              use12Hours={false}
              needConfirm={false}
              showNow={false}
              className="w-100 height-26 border-color-dcdcdc border-radius-1"
              disabledTime={(date) =>
                handleDisabledTime(date, `${record.key}.${record.index}.${value?.recruitmentTypeId}`)
              }
            />
          </Form.Item>
        );
      },
    }));

    _columns.unshift({
      rowScope: 'row',
      dataIndex: 'key',
      width: fixedLeftColumnWidth,
      key: 0,
      fixed: 'left',
      render: (value, record) => intl.formatMessage({ id: `workHours.${value}` }, { index: record.index + 1 }),
    });

    _columns.push({
      key: 1,
      width: fixedRightColumnWidth,
      fixed: 'right',
      onCell: (_, index) => {
        if (Number(index || 0) >= 2) return { rowSpan: 2 };
        else return { rowSpan: 1 };
      },
      render: (value, record) => {
        return (
          value.type === WorkHoursTypeEnum.BreakHours &&
          value.key.includes(WorkHoursPosition.START) && (
            <ButtonWrap
              variant="text"
              width={24}
              height={24}
              className="mx-auto"
              onClick={() => handleRemoveBreakHours(record as WorkHoursResponseDto, dataSource)}
            >
              <SvgDelete />
            </ButtonWrap>
          )
        );
      },
    });

    return _columns;
  }, [recruitmentTypeData?.data?.data?.content, intl, dataSource]);

  useEffect(() => {
    if (workHoursData?.data?.data) {
      if (Array.isArray(workHoursData?.data?.data?.workHours) && workHoursData?.data?.data?.workHours?.length > 0) {
        const workHours = workHoursData?.data?.data?.workHours;

        const _dataSource = workHours.flatMap((item) => [
          { ...item, key: `${item.key}.${WorkHoursPosition.START}` },
          { ...item, key: `${item.key}.${WorkHoursPosition.END}` },
        ]);

        setDataSource(_dataSource);
      }

      if (workHoursData?.data?.data?.general && Object.keys(workHoursData?.data?.data?.general).length > 0) {
        const { nightOvertimeStart, nightOvertimeEnd, ...otherData } = workHoursData?.data?.data?.general || {};

        form.setFieldsValue({
          ...otherData,
          nightOvertimeStart: nightOvertimeStart ? dayjs(nightOvertimeStart, TIME_FORMAT) : undefined,
          nightOvertimeEnd: nightOvertimeEnd ? dayjs(nightOvertimeEnd, TIME_FORMAT) : undefined,
        });
      }
    }
  }, [workHoursData?.data?.data]);

  useEffect(() => {
    if (dataSource.length > 0) {
      const prevFormValues = form.getFieldsValue();

      const formValues = dataSource.reduce((acc, curr) => {
        // filter to get keys are recruitment types
        const dataSourceKeys = Object.keys(curr).filter((key) => key !== 'index' && key !== 'key' && key !== 'type');

        dataSourceKeys.forEach((key) => {
          const formKey = `${curr.key}.${curr.index}.${curr[key]?.recruitmentTypeId}`;

          const startTime = curr[key].startTime ? dayjs(curr[key].startTime, TIME_FORMAT) : undefined;
          const endTime = curr[key].endTime ? dayjs(curr[key].endTime, TIME_FORMAT) : undefined;

          acc[formKey] = curr.key.includes(WorkHoursPosition.START) ? startTime : endTime;
        });

        return acc;
      }, {});

      form.setFieldsValue(
        !isChangeBreakHourRef.current ? { ...prevFormValues, ...formValues } : { ...formValues, ...prevFormValues },
      );
    }
  }, [dataSource]);

  const handleAddBreakHours = () => {
    isChangeBreakHourRef.current = true;

    let _dataSource = [...dataSource];
    const maxIndexBreakHours = _.maxBy(_dataSource, (dataSource) => dataSource.index)?.index || 0;
    const findBreakHours = _dataSource.filter(
      (dataSource) => dataSource.type === WorkHoursTypeEnum.BreakHours && dataSource.index === maxIndexBreakHours,
    );

    let newBreakHours: WorkHoursResponseDto[] = [];

    if (findBreakHours.length > 0) {
      newBreakHours = findBreakHours.map((dataSource) => {
        const recruitmentTypes = (
          Array.isArray(recruitmentTypeData?.data?.data?.content) ? recruitmentTypeData?.data?.data?.content : []
        )?.reduce((acc, curr) => {
          acc[curr.type] = {
            startTime: '',
            endTime: '',
            type: WorkHoursTypeEnum.BreakHours,
            recruitmentType: curr,
            recruitmentTypeId: curr.id,
          };

          return acc;
        }, {});

        return {
          key: dataSource.key,
          type: dataSource.type,
          index: maxIndexBreakHours + 1,
          ...recruitmentTypes,
        };
      });
    } else {
      const recruitmentTypes = (
        Array.isArray(recruitmentTypeData?.data?.data?.content) ? recruitmentTypeData?.data?.data?.content : []
      )?.reduce((acc, curr) => {
        acc[curr.type] = {
          startTime: '',
          endTime: '',
          type: WorkHoursTypeEnum.BreakHours,
          recruitmentType: curr,
          recruitmentTypeId: curr.id,
        };

        return acc;
      }, {});

      newBreakHours.push({
        key: `${WorkHoursTypeEnum.BreakHours}.${WorkHoursPosition.START}`,
        type: WorkHoursTypeEnum.BreakHours,
        index: 0,
        ...recruitmentTypes,
      });

      newBreakHours.push({
        key: `${WorkHoursTypeEnum.BreakHours}.${WorkHoursPosition.END}`,
        type: WorkHoursTypeEnum.BreakHours,
        index: 0,
        ...recruitmentTypes,
      });
    }
    _dataSource = [..._dataSource, ...newBreakHours];
    setDataSource(_dataSource);
  };

  const handleRemoveBreakHours = (record: WorkHoursResponseDto, dataSource: WorkHoursResponseDto[]) => {
    isChangeBreakHourRef.current = true;

    const _dataSource = [...dataSource].filter(
      (data) => data.type !== WorkHoursTypeEnum.BreakHours || data.index !== record?.index,
    );

    const reIndex = (dataSource: WorkHoursResponseDto[]) => {
      const keyCounters = {};

      dataSource.forEach((item) => {
        const key = item.key;

        if (!keyCounters.hasOwnProperty(key)) {
          keyCounters[key] = 0;
        }

        item.index = keyCounters[key];

        keyCounters[key]++;
      });

      return dataSource;
    };

    const reIndexDataSource = reIndex(_dataSource);

    const recruitmentTypes = Array.isArray(recruitmentTypeData?.data?.data?.content)
      ? recruitmentTypeData?.data?.data?.content
      : [];
    const formValues = form.getFieldsValue();

    recruitmentTypes.forEach((type) => {
      formValues[`${WorkHoursTypeEnum.BreakHours}.${WorkHoursPosition.START}.${record.index}.${type.id}`] = undefined;
      formValues[`${WorkHoursTypeEnum.BreakHours}.${WorkHoursPosition.END}.${record.index}.${type.id}`] = undefined;
    });

    form.setFieldsValue(formValues);

    setDataSource(reIndexDataSource);
  };

  const validateBreakHourTime = (values: CreateWorkHoursDto[]) => {
    const validates: Array<{ name: string; errors: string[] }> = [];

    const groupByRecruitmentTypeId = _.groupBy(values, (workHour) => workHour.recruitmentTypeId);

    Object.keys(groupByRecruitmentTypeId).forEach((key) => {
      const workHour = groupByRecruitmentTypeId[key].find((workHour) => workHour.type === WorkHoursTypeEnum.WorkHours);
      const breakHours = groupByRecruitmentTypeId[key].filter(
        (workHour) => workHour.type === WorkHoursTypeEnum.BreakHours && workHour.startTime && workHour.endTime,
      );

      if (workHour?.startTime && workHour?.endTime && breakHours.length > 0) {
        const workStartTime = dayjs(workHour.startTime, TIME_FORMAT);
        const workEndTime = dayjs(workHour.endTime, TIME_FORMAT);

        const isOvernightShift = workStartTime.isAfter(workEndTime) || workStartTime.isSame(workEndTime);

        const totalWorkHour = isOvernightShift
          ? dayjs(workHour.endTime, TIME_FORMAT).add(1, 'day').diff(dayjs(workHour.startTime, TIME_FORMAT), 'hours')
          : dayjs(workHour.endTime, TIME_FORMAT).diff(dayjs(workHour.startTime, TIME_FORMAT), 'hours');
        // breakHours.forEach((breakHour) => {
        //   const breakStartTime = dayjs(breakHour.startTime, TIME_FORMAT);
        //   const breakEndTime = dayjs(breakHour.endTime, TIME_FORMAT);

        //   let isValidBreakTime = false;

        //   if (isOvernightShift) {
        //     const midnight = dayjs('24:00', TIME_FORMAT);
        //     const nextDayStart = dayjs('00:00', TIME_FORMAT);

        //     const isBeforeMidnight =
        //       (breakStartTime.isAfter(workStartTime) && breakEndTime.isBefore(midnight)) ||
        //       breakEndTime.isSame(midnight);

        //     const isAfterMidnight =
        //       (breakStartTime.isAfter(nextDayStart) || breakStartTime.isSame(nextDayStart)) &&
        //       breakEndTime.isBefore(workEndTime);

        //     isValidBreakTime = isBeforeMidnight || isAfterMidnight;
        //   } else {
        //     isValidBreakTime = breakStartTime.isAfter(workStartTime) && breakEndTime.isBefore(workEndTime);
        //   }

        //   if (!isValidBreakTime) {
        //     validates.push(
        //       {
        //         name: `${WorkHoursTypeEnum.BreakHours}.${WorkHoursPosition.START}.${breakHour?.index}.${key}`,
        //         errors: [intl.formatMessage({ id: 'workHours.breakHours.notWithin' })],
        //       },
        //       {
        //         name: `${WorkHoursTypeEnum.BreakHours}.${WorkHoursPosition.END}.${breakHour?.index}.${key}`,
        //         errors: [intl.formatMessage({ id: 'workHours.breakHours.notWithin' })],
        //       },
        //     );
        //   }
        // });

        const totalBreakHours = breakHours.reduce((totalMinutes, breakHour) => {
          const breakStartTime = dayjs(breakHour.startTime, TIME_FORMAT);
          const breakEndTime = dayjs(breakHour.endTime, TIME_FORMAT);
          const minutes = breakEndTime.isBefore(breakStartTime)
            ? breakEndTime.add(1, 'day').diff(breakStartTime, 'hours')
            : breakEndTime.diff(breakStartTime, 'hours');

          return totalMinutes + Number(minutes || 0);
        }, 0);
        if (totalWorkHour < totalBreakHours) {
          validates.concat(
            breakHours.map((breakHour) => ({
              name: `${WorkHoursTypeEnum.BreakHours}.${WorkHoursPosition.START}.${breakHour?.index}.${key}`,
              errors: [intl.formatMessage({ id: 'workHours.TOO_MUCH_BREAK_HOURS' })],
            })),
          );
        }
      }
    });

    return validates;
  };

  const onFinish = (values: Record<string, Dayjs | string | number | undefined>) => {
    const payload = Object.keys(values)
      .filter((value) => value.includes(WorkHoursTypeEnum.WorkHours) || value.includes(WorkHoursTypeEnum.BreakHours))
      .map((key) => {
        const [type, position, index, recruitmentTypeId] = key.split('.');

        let startTime: string | undefined;
        let endTime: string | undefined;

        if (position === WorkHoursPosition.START) {
          startTime = values?.[key] as string;
          endTime = values?.[key.replace(WorkHoursPosition.START, WorkHoursPosition.END)] as string;
        } else if (position === WorkHoursPosition.END) {
          startTime = values?.[key.replace(WorkHoursPosition.END, WorkHoursPosition.START)] as string;
          endTime = values?.[key] as string;
        }

        return {
          type,
          index: +index,
          recruitmentTypeId,
          startTime: startTime ? dayjs(startTime).format(TIME_FORMAT) : null,
          endTime: endTime ? dayjs(endTime).format(TIME_FORMAT) : null,
        };
      });

    const uniquePayload = _.uniqWith(payload, _.isEqual);

    form.setFields(Object.keys(values).map((name) => ({ name, errors: [] })));
    const validates = validateBreakHourTime(uniquePayload as CreateWorkHoursDto[]);

    if (validates.length > 0) {
      return form.setFields(validates);
    }

    const data = uniquePayload as CreateWorkHoursDto[];
    const general: UpdateGeneralWorkingScheduleDto = {
      dayStartOfMonth: Number(values?.dayStartOfMonth),
      dayEndOfMonth: Number(values?.dayEndOfMonth),
      overtimePeriod: Number(values?.overtimePeriod),
      nightOvertimeStart: dayjs(values?.nightOvertimeStart)?.format(TIME_FORMAT),
      nightOvertimeEnd: dayjs(values?.nightOvertimeEnd)?.format(TIME_FORMAT),
    } as UpdateGeneralWorkingScheduleDto;
    saveWorkHoursMutation.mutate({ data, general });
  };

  const validatePositiveIntegerWithRange = (min?, max?) => (_, value) => {
    if (!value || !/^[1-9]\d*$/.test(value)) {
      return Promise.reject(intl.formatMessage({ id: 'workHours.required' }));
    }
    if (value < min || value > max) {
      return Promise.reject();
    }
    return Promise.resolve();
  };

  return (
    <div className="work-hours">
      <Widget
        breadcrumbs={[
          intl.formatMessage({ id: 'adminMenu.configurationManagement' }),
          intl.formatMessage({ id: 'workHours.title' }),
        ]}
        title={intl.formatMessage({ id: 'workHours.title' })}
        content={
          <Form form={form} onFinish={onFinish} colon={false} layout="vertical" requiredMark={false}>
            <Flex gap={24} vertical>
              <Row className="work-hours-content-row" wrap align="middle" justify="space-between" gutter={[24, 24]}>
                <Col>
                  <Row gutter={[8, 8]} align="middle" wrap={false}>
                    <Col className="work-hours-content-row-col-1 bg-white">
                      <Form.Item
                        className="mb-0"
                        name="dayStartOfMonth"
                        label={intl.formatMessage({ id: 'workHours.month.start' })}
                        rules={[
                          {
                            validator: validatePositiveIntegerWithRange(2, 28),
                          },
                        ]}
                      >
                        <InputWrap
                          className="work-hours-content-row-col-1-input height-26 border-color-dcdcdc border-radius-1"
                          max={28}
                          min={2}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            if (value >= 2 && value <= 28) {
                              form.setFieldsValue({ dayEndOfMonth: value - 1 });
                            }
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <div className="work-hours-content-row-line">-</div>
                    <Col className="work-hours-content-row-col-1 bg-white">
                      <Form.Item
                        className="mb-0"
                        name="dayEndOfMonth"
                        label={intl.formatMessage({ id: 'workHours.month.end' })}
                        rules={[
                          {
                            validator: validatePositiveIntegerWithRange(1, 27),
                          },
                        ]}
                      >
                        <InputWrap
                          className="work-hours-content-row-col-1-input height-26 border-color-dcdcdc border-radius-1"
                          max={27}
                          min={1}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            if (value >= 1 && value <= 27) {
                              form.setFieldsValue({ dayStartOfMonth: value + 1 });
                            }
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
                <Col className="work-hours-content-row-col-2 bg-white">
                  <Form.Item
                    className="mb-0"
                    name="overtimePeriod"
                    label={intl.formatMessage({ id: 'workHours.OVERTIME' })}
                    rules={[
                      {
                        validator: validatePositiveIntegerWithRange(),
                      },
                    ]}
                  >
                    <InputWrap className="height-26 border-color-dcdcdc border-radius-1" />
                  </Form.Item>
                </Col>
                <Col className="work-hours-content-row-col-3 bg-white">
                  <Form.Item
                    className="mb-0"
                    name="nightOvertimeStart"
                    label={intl.formatMessage({ id: 'workHours.NIGHT_OVERTIME.START' })}
                    rules={[
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value && getFieldValue('nightOvertimeEnd'))
                            return Promise.reject(intl.formatMessage({ id: 'workHours.required' }));
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <TimePicker
                      format={TIME_FORMAT}
                      use12Hours={false}
                      needConfirm={false}
                      showNow={false}
                      className="w-100 height-26 border-color-dcdcdc border-radius-1"
                    />
                  </Form.Item>
                </Col>
                <Col className="work-hours-content-row-col-3 bg-white">
                  <Form.Item
                    className="mb-0"
                    name="nightOvertimeEnd"
                    label={intl.formatMessage({ id: 'workHours.NIGHT_OVERTIME.END' })}
                    rules={[
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value && getFieldValue('nightOvertimeStart'))
                            return Promise.reject(intl.formatMessage({ id: 'workHours.required' }));
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <TimePicker
                      format={TIME_FORMAT}
                      use12Hours={false}
                      needConfirm={false}
                      showNow={false}
                      className="w-100 height-26 border-color-dcdcdc border-radius-1"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <TableWrap
                isPagination={false}
                loading={isLoadingWorkHours || isLoadingRecruitmentType}
                tableWrapperRef={tableWrapperRef}
                tableProps={{
                  dataSource,
                  columns,
                  scroll: {
                    x: Number(columns?.[1]?.width || 0) === defaultOtherColumnWidth ? 1000 : true,
                  },
                  rowKey: (record) => `${record.key}_${record.index}`,
                  footer: () => (
                    <Flex align="center" justify="center">
                      <ButtonWrap
                        variant="text"
                        onClick={handleAddBreakHours}
                        height={40}
                        disabled={!recruitmentTypeData?.data?.data?.content?.length}
                      >
                        <SvgAddPrimary />
                        {intl.formatMessage({ id: 'workHours.add' })}
                      </ButtonWrap>
                    </Flex>
                  ),
                }}
              />

              <ButtonWrap disabled={saveWorkHoursMutation.isPending} width={162} htmlType="submit" className="ms-auto">
                {intl.formatMessage({ id: 'workHours.save' })}
              </ButtonWrap>
            </Flex>
          </Form>
        }
      />
    </div>
  );
};

export default WorkHours;
